import React from 'react';
import {
  Autocomplete,
  Alert,
  alpha,
  createTheme,
  Box,
  Card,
  CardActions,
  Divider,
  SvgIcon,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip
} from '@mui/material';
import { CircularProgress, Paper, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BarChartIcon from '@mui/icons-material/BarChart';
import { format } from 'date-fns';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Chart from './chart';
import { ChartOptions_bar1, ChartOptions_line } from './ChartOptions';
import Client from '../Client';
import BackofficeReportsApi from './backofficeReportsApi';
import BackOfficeConfig from '../../Config';

let theme = createTheme();

const objectToCsv = function (data) {
  const csvRows = [];
  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return `"${val}"`;
    });
    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

const MenuProps = {
  PaperProps: { style: { maxHeight: 48 * 5 } },
};

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function chartNumber(value) {
  if ((typeof value !== 'number' && typeof value !== 'string')
      || (typeof value === 'string' && value.trim() === '')) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function chartRatio(value, divisor) {
  const numerator = chartNumber(value);
  const denominator = chartNumber(divisor);
  if (numerator === null || denominator === null || denominator === 0) return null;
  return chartNumber(numerator / denominator);
}

function chartLabel(value, decimals = 2) {
  const number = chartNumber(value);
  if (number === null) return '';
  return number > 1000 ? `${thousandsSeparator((number / 1000).toFixed(1))}K` : number.toFixed(decimals);
}

function chartMaximum(...series) {
  const values = series.flat().map(({ y }) => y).filter(Number.isFinite);
  const max = parseInt(Math.max(...values));
  if (!Number.isFinite(max) || max <= 0) return 1;
  const power = max.toString().length - 1;
  return Math.ceil(max / 10 ** power) * 10 ** power;
}

// Keep the shared chart layout, with safe formatters scoped to this report.
const salesChartOptions = {
  ...ChartOptions_line,
  yaxis: ChartOptions_line.yaxis.map((axis, index) => ({
    ...axis,
    labels: { ...axis.labels, formatter: value => chartLabel(value, index === 3 ? 1 : 2) },
  })),
};
const dailyChartOptions = {
  ...ChartOptions_bar1,
  yaxis: {
    ...ChartOptions_bar1.yaxis,
    labels: { ...ChartOptions_bar1.yaxis.labels, formatter: value => chartLabel(value, 0) },
  },
};

function generateYearsBetween(startYear = 2000, endYear) {
  const endDate = endYear || new Date().getFullYear();
  let years = [];

  while (startYear <= endDate) {
    years.push(startYear);
    startYear++;
  }

  return years;
}

let end = 0;
let start = 0;

end = new Date().getFullYear();
start = end - 3;

const yearsArray = generateYearsBetween(start, end);
yearsArray.reverse();

let years = {};
years = yearsArray.map(x => ({ value: x }));

const now0 = new Date();
now0.setHours(0, 0, 0, 0);
const now = now0;
const csvdate = format(now, 'yyyy-MM-dd');

class OverviewProductSalesMultiple extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      location: [0],
      product: [0],
      delivery: 2,
      year: new Date().getFullYear(),
      value: "1",
      locations: [{ 'iref': 0, 'name': "All Locations" }],
      products: [{ 'iref': 0, 'name': "All Products" }],
      deliverys: [{ 'iref': 2, 'name': "All" }, { 'iref': 0, 'name': "Restaurant" }, { 'iref': 1, 'name': "Delivery" }, { 'iref': 3, 'name': "App Clients" }],
      filterData: {},
      sales_month: null,
      reportError: null,
      busyOverlay: false,
    };

    this.pushData = this.pushData.bind(this);
    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.selectYear = this.selectYear.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.selectProduct = this.selectProduct.bind(this);
    this.selectDelivery = this.selectDelivery.bind(this);

    this.showOverlay = this.showOverlay.bind(this);
    this.hideOverlayMin = this.hideOverlayMin.bind(this);
    this.pushDataWithOverlay = this.pushDataWithOverlay.bind(this);
    this._overlayShownAt = 0;

    this.queryData = {};
  }

  componentDidMount() {
    this.getData();
    this.pushDataWithOverlay();
  }

  getData() {
    let data = {
      "location_status": 1,
      "isForSale": 1,
      "orderBy_name": true
    };

    this.setState({ filterData: data });

    let locations = BackofficeReportsApi.getLocations(this.state.client);
    this.setState({ locations: locations.locations });

    let products = BackofficeReportsApi.getProductsCatalogSummary(this.state.client);
    this.setState({ products: products.products });
  }

  pushData() {
    let queryData = {
      year: this.state.year,
      location: this.state.location,
      delivery: this.state.delivery,
      location_status: 1,
      product: this.state.product,
    };

    try {
      const data = BackofficeReportsApi.postSalesProductTrends(this.state.client, queryData);
      this.setState({ sales_month: data, reportError: null });
    } catch (error) {
      this.setState({ sales_month: null, reportError: 'Unable to load product sales. Please try again.' });
    }
  }

  doSync() {
    this.pushDataWithOverlay();
  }

  selectYear = (event) => {
    this.setState(
      { year: event.target.value },
      function () {
        this.doSync();
      }
    );
  }

  selectLocation = (event) => {
    this.setState(
      { location: event.target.value },
      function () {
        if (Number(this.state.location[0]) === 0) {
          delete this.state.location[0];
        }

        if (Number(this.state.location.slice(-1)) === 0) {
          this.setState({ location: [] });
          this.setState({ location: [0] });
        }

        this.doSync();
      }
    );
  }

  selectDelivery = (event) => {
    this.setState(
      { delivery: event.target.value },
      function () {
        this.doSync();
      }
    );
  }

  selectProduct = (event) => {
    this.setState(
      { product: event.target.value },
      function () {
        if (Number(this.state.product[0]) === 0) {
          delete this.state.product[0];
        }

        if (Number(this.state.product.slice(-1)) === 0) {
          this.setState({ product: [] });
          this.setState({ product: [0] });
        }

        this.doSync();
      }
    );
  }

  handleChange = (event, newValue) => {
    this.setState({ value: newValue });
  }

  onDownload = () => {
    if (!this.state.sales_month?.sales?.length || this.state.reportError) return;
    const link = document.createElement("a");
    let csvData = 0;

    link.download = "salesProductsOverview_" + csvdate + ".csv";

    if (this.state.sales_month) {
      csvData = objectToCsv(this.state.sales_month.sales);
    }

    let csvHeader = "Sales Products Overview\nLocations: " + this.state.location + "\nProducts: " + this.state.product;
    let csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  async showOverlay() {
    this.setState({ busyOverlay: true });
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    this._overlayShownAt = performance.now();
  }

  hideOverlayMin() {
    const MIN_VISIBLE = 400;
    const elapsed = performance.now() - (this._overlayShownAt || 0);
    const hide = () => this.setState({ busyOverlay: false });

    if (elapsed < MIN_VISIBLE) {
      setTimeout(hide, MIN_VISIBLE - elapsed);
    } else {
      hide();
    }
  }

  async pushDataWithOverlay() {
    await this.showOverlay();

    try {
      await this.pushData();
    } finally {
      this.hideOverlayMin();
    }
  }

  render() {
    let locations = [{ 'iref': 0, 'name': "All Locations" }];

    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];
        let myData = { 'iref': fData.iref, 'name': fData.name };
        locations.push(myData);
      });
    }

    let products = [{ 'iref': 0, 'name': "All Products" }];

    if (this.state.products) {
      Object.keys(this.state.products).forEach(key => {
        let fData = this.state.products[key];
        let myData = { 'iref': fData.iref, 'name': fData.name };
        products.push(myData);
      });
    }

    let deliverys = [
      { 'iref': 2, 'name': "All" },
      { 'iref': 0, 'name': "Restaurant" },
      { 'iref': 1, 'name': "Delivery" },
      { 'iref': 3, 'name': "App Clients" }
    ];

    let sales = [];
    let salesT = [];
    let salesA = [];
    let salesP = [];
    let salesD = [];
    let salesD_LY = [];
    let salesM_new = [];
    let salesM_LY_new = [];
    let salesW = [];
    let salesW_LY = [];
    let salesW_trans = [];
    let salesW_LY_trans = [];
    let salesM_new_trans = [];
    let salesM_LY_new_trans = [];

    if (this.state.sales_month && !this.state.reportError) {
      if (this.state.sales_month.sales) {
        Object.keys(this.state.sales_month.sales).forEach(key => {
          let fData = this.state.sales_month.sales[key];
          let sign = "";

          if (fData.month < 10) {
            sign = "-0";
          } else {
            sign = "-";
          }

          let myData = { 'x': fData.year + sign + fData.month, 'y': chartNumber(fData.total) };
          sales.push(myData);

          let myTrans = { 'x': fData.year + sign + fData.month, 'y': chartNumber(fData.trans) };
          salesT.push(myTrans);

          let myAvg = { 'x': fData.year + sign + fData.month, 'y': chartRatio(fData.total, fData.trans) };
          salesA.push(myAvg);

          let tzx = chartRatio(fData.total, fData.tot);
          if (tzx !== null) {
            tzx = chartNumber(tzx * 100);
            if (tzx !== null) tzx = tzx.toFixed(2);
          }

          if (tzx < 0) tzx = 0;
          if (tzx > 100) tzx = 100;

          let myPct = { 'x': fData.year + sign + fData.month, 'y': tzx };
          salesP.push(myPct);
        });
      }

      if (this.state.sales_month.salesD) {
        let days = "";

        Object.keys(this.state.sales_month.salesD).forEach(key => {
          days = BackOfficeConfig.WEEKDAYS_EN[parseInt(key) + 1];
          let fData = this.state.sales_month.salesD[key];
          let myData = { 'x': days, 'y': chartRatio(fData.total, fData.count) };
          salesD.push(myData);
        });
      }

      if (this.state.sales_month.salesD_LY) {
        let days = "";

        Object.keys(this.state.sales_month.salesD_LY).forEach(key => {
          days = BackOfficeConfig.WEEKDAYS_EN[parseInt(key) + 1];
          let fData = this.state.sales_month.salesD_LY[key];
          let myData = { 'x': days, 'y': chartRatio(fData.total, fData.count) };
          salesD_LY.push(myData);
        });
      }

      if (this.state.sales_month.salesM_new) {
        Object.keys(this.state.sales_month.salesM_new).forEach(key => {
          let fData = this.state.sales_month.salesM_new[key];

          let myData = { 'x': fData.month, 'y': chartNumber(fData.total) };
          salesM_new.push(myData);

          let myTrans = { 'x': fData.month, 'y': chartNumber(fData.trans) };
          salesM_new_trans.push(myTrans);
        });
      }

      if (this.state.sales_month.salesM_LY_new) {
        Object.keys(this.state.sales_month.salesM_LY_new).forEach(key => {
          let fData = this.state.sales_month.salesM_LY_new[key];

          let myData = { 'x': fData.month, 'y': chartNumber(fData.total) };
          salesM_LY_new.push(myData);

          let myTrans = { 'x': fData.month, 'y': chartNumber(fData.trans) };
          salesM_LY_new_trans.push(myTrans);
        });
      }

      if (this.state.sales_month.salesW) {
        Object.keys(this.state.sales_month.salesW).forEach(key => {
          let fData = this.state.sales_month.salesW[key];

          let myData = { 'x': fData.week, 'y': chartNumber(fData.total) };
          salesW.push(myData);

          let myTrans = { 'x': fData.week, 'y': chartNumber(fData.trans) };
          salesW_trans.push(myTrans);
        });
      }

      if (this.state.sales_month.salesW_LY) {
        Object.keys(this.state.sales_month.salesW_LY).forEach(key => {
          let fData = this.state.sales_month.salesW_LY[key];

          let myData = { 'x': fData.week, 'y': chartNumber(fData.total) };
          salesW_LY.push(myData);

          let myTrans = { 'x': fData.week, 'y': chartNumber(fData.trans) };
          salesW_LY_trans.push(myTrans);
        });
      }
    }

    const maxM = chartMaximum(salesM_LY_new, salesM_new);
    const maxM_trans = chartMaximum(salesM_LY_new_trans, salesM_new_trans);
    const maxWm = chartMaximum(salesW_LY, salesW);
    const maxWm_trans = chartMaximum(salesW_LY_trans, salesW_trans);

    const chartHeight =
      window.innerWidth < 600
        ? 320
        : window.innerWidth < 1200
          ? 380
          : 430;

    const controlSx = {
      '& .MuiInputBase-root': {
        minHeight: 38,
        borderRadius: '12px',
        fontSize: {
          xs: '0.82rem',
          sm: '0.88rem',
        },
        bgcolor: '#fff',
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
      },
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
      },
    };

    const smallControlSx = {
      ...controlSx,
      width: {
        xs: 'calc(50% - 6px)',
        sm: 120,
      },
    };

    const mediumControlSx = {
      ...controlSx,
      width: {
        xs: 'calc(50% - 6px)',
        sm: 180,
      },
    };

    const productControlSx = {
      width: {
        xs: '100%',
        sm: 300,
        md: 360,
      },
      '& .MuiInputBase-root': {
        minHeight: 38,
        borderRadius: '12px',
        fontSize: {
          xs: '0.82rem',
          sm: '0.88rem',
        },
        bgcolor: '#fff',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: 0.5,
      },
      '& .MuiAutocomplete-input': {
        minWidth: '48px !important',
        fontSize: {
          xs: '1rem',
          sm: '0.82rem',
        },
        padding: '2px 0',
      },
      '& .MuiAutocomplete-option': {
        fontSize: '0.8rem',
        minHeight: '28px',
      },
    };

    return (
      <Card
        variant="outlined"
        sx={{
          position: 'relative',
          width: '100%',
          height: {
            xs: 600,
            sm: 600,
            md: 620,
            lg: 640,
          },
          maxHeight: {
            xs: 600,
            sm: 600,
            md: 620,
            lg: 640,
          },
          minHeight: 0,
          borderRadius: {
            xs: '14px',
            sm: '16px',
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            px: {
              xs: 0.75,
              sm: 1,
            },
            py: {
              xs: 0.65,
              sm: 0.85,
            },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: {
                xs: 'stretch',
                sm: 'center',
              },
              justifyContent: 'space-between',
              gap: {
                xs: 0.75,
                sm: 1,
              },
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Product Sales Overview
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.72rem',
                    sm: '0.78rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Product sales, transactions and weekday analysis
              </Typography>
            </Box>

            <Tabs
              value={this.state.value}
              onChange={this.handleChange}
              aria-label="product sales overview tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 36,
                maxWidth: {
                  xs: '100%',
                  sm: 360,
                },
                '& .MuiTabs-flexContainer': {
                  gap: 0.5,
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
                '& .MuiTab-root': {
                  minHeight: 34,
                  minWidth: {
                    xs: 58,
                    sm: 70,
                  },
                  px: {
                    xs: 1,
                    sm: 1.25,
                  },
                  py: 0,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 750,
                  fontSize: {
                    xs: '0.76rem',
                    sm: '0.84rem',
                  },
                  color: '#64748b',
                  border: '1px solid transparent',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#1a237e',
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                  borderColor: 'rgba(26, 35, 126, 0.14)',
                },
              }}
            >
              <Tab icon={<BarChartIcon sx={{ fontSize: 18 }} />} value="1" />
              <Tab label="Month" value="2" />
              <Tab label="Week" value="3" />
              <Tab label="wDay" value="4" />
            </Tabs>
          </Box>

          <Box
            sx={{
              mt: 0.85,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: {
                xs: 0.75,
                sm: 1,
              },
              maxHeight: {
                xs: 150,
                sm: 112,
                md: 96,
              },
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 0.25,
              '&::-webkit-scrollbar': {
                width: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(15, 23, 42, 0.18)',
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
              },
            }}
          >
            <TextField
              id="standard-select-years"
              select
              defaultValue={end}
              variant="outlined"
              size="small"
              sx={smallControlSx}
              onChange={this.selectYear}
            >
              {years.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              id="standard-select-location"
              select
              variant="outlined"
              size="small"
              sx={mediumControlSx}
              SelectProps={{
                multiple: true,
                value: this.state.location,
                onChange: this.selectLocation,
                renderValue: (selected) =>
                  selected
                    .map((v) => locations.find((l) => l.iref === v)?.name ?? v)
                    .join(', '),
                MenuProps,
              }}
            >
              {locations.map((option) => {
                const checked = this.state.location.indexOf(option.iref) > -1;

                return (
                  <MenuItem key={option.iref} value={option.iref}>
                    <Checkbox checked={checked} size="small" />
                    <ListItemText
                      primary={option.name}
                      primaryTypographyProps={{
                        fontWeight: checked ? 600 : 400,
                        noWrap: true,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </TextField>

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={products}
              getOptionLabel={(option) => option.name}
              value={products.filter(p => (this.state.product || []).includes(p.iref))}
              onChange={(event, newValue) =>
                this.selectProduct({ target: { value: newValue.map(p => p.iref) } })
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} size="small" />
                  {option.name}
                </li>
              )}
              renderTags={(value) => (
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    pr: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                  }}
                  title={value.map(v => v.name).join(', ')}
                >
                  {value.map(v => v.name).join(', ')}
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.iref === value.iref}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Products"
                />
              )}
              sx={productControlSx}
            />

            <TextField
              id="standard-select-delivery"
              select
              defaultValue="2"
              variant="outlined"
              size="small"
              sx={smallControlSx}
              onChange={this.selectDelivery}
            >
              {deliverys.map((option) => (
                <MenuItem key={option.iref} value={option.iref}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            bgcolor: '#fff',
            p: {
              xs: 0.75,
              sm: 1,
            },
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(15, 23, 42, 0.18)',
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
            },
          }}
        >
          <Box
            sx={{
              borderRadius: '14px',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              overflow: 'hidden',
              bgcolor: '#fff',
              minWidth: {
                xs: 540,
                sm: '100%',
              },
              minHeight: {
                xs: 330,
                sm: 390,
                md: 430,
                lg: 460,
              },
            }}
          >
            {this.state.reportError && <Alert severity="error">{this.state.reportError}</Alert>}

            {this.state.value === "1" &&
              sales.length !== 0 && salesT.length !== 0 &&
              salesA !== 0 && salesP !== 0 ? (
                <Chart
                  height={chartHeight}
                  type="line0"
                  options={salesChartOptions}
                  series={[
                    {
                      name: 'Monthly sales',
                      type: 'bar',
                      data: sales
                    },
                    {
                      name: 'Monthly transactions',
                      type: 'line',
                      data: salesT
                    },
                    {
                      name: 'Average transaction',
                      type: 'line',
                      data: salesA
                    },
                    {
                      name: 'Percentage',
                      type: 'line',
                      data: salesP
                    }
                  ]}
                  width="100%"
                />
              ) : ""}

            {this.state.value === "2" && (salesM_new.length > 0 || salesM_LY_new.length > 0) ? (
              <Chart
                height={chartHeight}
                type="line11"
                options={{
                  chart: {
                    background: 'transparent',
                    stacked: false,
                    toolbar: {
                      show: false,
                      offsetX: 0,
                      offsetY: 0,
                      tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true | '<img src="/static/icons/reset.png" width="20">',
                        customIcons: []
                      }
                    },
                    export: {
                      csv: {
                        filename: undefined,
                        columnDelimiter: ',',
                        headerCategory: 'category',
                        headerValue: 'value',
                        dateFormatter(timestamp) {
                          return new Date(timestamp).toDateString();
                        }
                      },
                      svg: {
                        filename: undefined,
                      },
                      png: {
                        filename: undefined,
                      }
                    },
                    zoom: {
                      enabled: false,
                      type: 'x',
                      autoScaleYaxis: false,
                      zoomedArea: {
                        fill: {
                          color: '#90CAF9',
                          opacity: 0.4
                        },
                        stroke: {
                          color: '#0D47A1',
                          opacity: 0.4,
                          width: 1
                        }
                      }
                    }
                  },
                  colors: [alpha(theme.palette.primary.main, 0.25), theme.palette.primary.main],
                  dataLabels: {
                    enabled: false
                  },
                  fill: {
                    opacity: 1,
                    type: 'solid'
                  },
                  grid: {
                    borderColor: theme.palette.divider,
                    strokeDashArray: 2,
                    xaxis: {
                      lines: {
                        show: false
                      }
                    },
                    yaxis: {
                      lines: {
                        show: true
                      }
                    }
                  },
                  legend: {
                    show: false
                  },
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      columnWidth: '70%',
                      barHeight: '70%',
                      distributed: false,
                      colors: {
                        backgroundBarColors: Array
                      }
                    }
                  },
                  stroke: {
                    colors: [alpha(theme.palette.primary.main, 0.25), theme.palette.primary.main],
                    show: true,
                    curve: 'smooth',
                    lineCap: 'square',
                    width: 2
                  },
                  theme: {
                    mode: theme.palette.mode
                  },
                  xaxis: {
                    tickPlacement: 'on',
                    type: 'numeric',
                    min: 1,
                    max: 12,
                    tickAmount: 11,
                    axisBorder: {
                      color: theme.palette.divider,
                      show: true
                    },
                    axisTicks: {
                      color: theme.palette.divider,
                      show: true
                    },
                    labels: {
                      offsetY: 2,
                      formatter: (value) => chartNumber(value)?.toFixed(0) ?? '',
                      style: {
                        colors: theme.palette.text.secondary
                      }
                    }
                  },
                  markers: {
                    size: [0, 0, 3, 3]
                  },
                  yaxis: [
                    {
                      seriesName: 'Last year Value',
                      show: true,
                      min: 0,
                      max: maxM,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                      }
                    },
                    {
                      seriesName: 'This year Value',
                      show: false,
                      min: 0,
                      max: maxM,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                        style: {
                          colors: theme.palette.text.secondary
                        }
                      }
                    },
                    {
                      seriesName: 'Last year Transactions',
                      opposite: true,
                      min: 0,
                      max: maxM_trans,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                        style: {
                          colors: theme.palette.text.secondary
                        }
                      }
                    },
                    {
                      seriesName: 'This year Transactions',
                      opposite: true,
                      show: false,
                      min: 0,
                      max: maxM_trans,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                        style: {
                          colors: theme.palette.text.secondary
                        }
                      }
                    }
                  ]
                }}
                series={[
                  {
                    name: 'Last year Value',
                    data: salesM_LY_new,
                    type: 'bar'
                  },
                  {
                    name: 'This year Value',
                    data: salesM_new,
                    type: 'bar'
                  },
                  {
                    name: 'Last year Transactions',
                    data: salesM_LY_new_trans,
                    type: 'line'
                  },
                  {
                    name: 'This year Transactions',
                    data: salesM_new_trans,
                    type: 'line',
                  }
                ]}
                width="100%"
              />
            ) : ""}

            {this.state.value === "3" && (salesW.length > 0 || salesW_LY.length > 0) ? (
              <Chart
                height={chartHeight}
                type="line11"
                options={{
                  chart: {
                    background: 'transparent',
                    stacked: false,
                    toolbar: {
                      show: false,
                      offsetX: 0,
                      offsetY: 0,
                      tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true | '<img src="/static/icons/reset.png" width="20">',
                        customIcons: []
                      }
                    },
                    export: {
                      csv: {
                        filename: undefined,
                        columnDelimiter: ',',
                        headerCategory: 'category',
                        headerValue: 'value',
                        dateFormatter(timestamp) {
                          return new Date(timestamp).toDateString();
                        }
                      },
                      svg: {
                        filename: undefined,
                      },
                      png: {
                        filename: undefined,
                      }
                    },
                    zoom: {
                      enabled: false,
                      type: 'x',
                      autoScaleYaxis: false,
                      zoomedArea: {
                        fill: {
                          color: '#90CAF9',
                          opacity: 0.4
                        },
                        stroke: {
                          color: '#0D47A1',
                          opacity: 0.4,
                          width: 1
                        }
                      }
                    }
                  },
                  colors: [alpha(theme.palette.primary.main, 0.25), theme.palette.primary.main],
                  dataLabels: {
                    enabled: false
                  },
                  fill: {
                    opacity: 1,
                    type: 'solid'
                  },
                  grid: {
                    borderColor: theme.palette.divider,
                    strokeDashArray: 2,
                    xaxis: {
                      lines: {
                        show: false
                      }
                    },
                    yaxis: {
                      lines: {
                        show: true
                      }
                    }
                  },
                  legend: {
                    show: false
                  },
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      columnWidth: '70%',
                      barHeight: '70%',
                      distributed: false,
                      colors: {
                        backgroundBarColors: Array
                      }
                    }
                  },
                  stroke: {
                    colors: [alpha(theme.palette.primary.main, 0.25), theme.palette.primary.main],
                    show: true,
                    curve: 'smooth',
                    lineCap: 'square',
                    width: 2
                  },
                  theme: {
                    mode: theme.palette.mode
                  },
                  xaxis: {
                    tickPlacement: 'on',
                    type: 'numeric',
                    tickAmount: 'dataPoints',
                    axisBorder: {
                      color: theme.palette.divider,
                      show: true
                    },
                    axisTicks: {
                      color: theme.palette.divider,
                      show: true
                    },
                    labels: {
                      offsetY: 2,
                      formatter: (value) => {
                        return chartNumber(value)?.toFixed(0) ?? '';
                      },
                      style: {
                        colors: theme.palette.text.secondary
                      }
                    }
                  },
                  markers: {
                    size: [0, 0, 3, 3]
                  },
                  yaxis: [
                    {
                      seriesName: 'Last year Value',
                      show: true,
                      min: 0,
                      max: maxWm,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                      }
                    },
                    {
                      seriesName: 'This year Value',
                      show: false,
                      min: 0,
                      max: maxWm,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                        style: {
                          colors: theme.palette.text.secondary
                        }
                      }
                    },
                    {
                      seriesName: 'Last year Transactions',
                      opposite: true,
                      min: 0,
                      max: maxWm_trans,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                        style: {
                          colors: theme.palette.text.secondary
                        }
                      }
                    },
                    {
                      seriesName: 'This year Transactions',
                      opposite: true,
                      show: false,
                      min: 0,
                      max: maxWm_trans,
                      labels: {
                        formatter: (value) => chartLabel(value),
                        offsetX: -10,
                        style: {
                          colors: theme.palette.text.secondary
                        }
                      }
                    }
                  ]
                }}
                series={[
                  {
                    name: 'Last year Value',
                    data: salesW_LY,
                    type: 'bar'
                  },
                  {
                    name: 'This year Value',
                    data: salesW,
                    type: 'bar'
                  },
                  {
                    name: 'Last year Transactions',
                    data: salesW_LY_trans,
                    type: 'line'
                  },
                  {
                    name: 'This year Transactions',
                    data: salesW_trans,
                    type: 'line'
                  }
                ]}
                width="100%"
              />
            ) : ""}

            {this.state.value === "4" && (salesD.length > 0 || salesD_LY.length > 0) ? (
              <Chart
                height={chartHeight}
                type="bar1"
                options={dailyChartOptions}
                series={[
                  {
                    name: 'Daily sales Last Year',
                    data: salesD_LY
                  },
                  {
                    name: 'Daily sales This Year',
                    data: salesD
                  }
                ]}
                width="100%"
              />
            ) : ""}
          </Box>
        </Box>

        <Divider />

        <CardActions
          sx={{
            flexShrink: 0,
            justifyContent: 'flex-end',
            px: {
              xs: 0.75,
              sm: 1,
            },
            py: {
              xs: 0.65,
              sm: 0.85,
            },
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
          }}
        >
          <Tooltip title="Download CSV">
            <span>
              <Button
                color="inherit"
                endIcon={(
                  <SvgIcon fontSize="small">
                    <ArrowForwardIcon />
                  </SvgIcon>
                )}
                size="small"
                onClick={this.onDownload}
                disabled={!this.state.sales_month?.sales?.length || Boolean(this.state.reportError)}
                variant="contained"
                sx={{
                  height: 36,
                  borderRadius: '12px',
                  color: '#1a237e',
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                  boxShadow: 'none',
                  border: '1px solid rgba(26, 35, 126, 0.12)',
                  textTransform: 'none',
                  fontWeight: 750,
                  '&:hover': {
                    bgcolor: 'rgba(26, 35, 126, 0.12)',
                    boxShadow: 'none',
                  },
                }}
              >
                Download
              </Button>
            </span>
          </Tooltip>
        </CardActions>

        {this.state.busyOverlay && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              pointerEvents: 'auto',
              bgcolor: 'rgba(15, 23, 42, 0.16)',
              backgroundImage:
                'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.18) 75%, rgba(15, 23, 42, 0.22) 100%)',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: '14px',
                bgcolor: 'background.paper',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <CircularProgress size={22} color="inherit" />
              <Typography variant="body2">Preparing report…</Typography>
            </Paper>
          </Box>
        )}
      </Card>
    );
  }
}

export default OverviewProductSalesMultiple;
